import { call, put, takeEvery, takeLatest, select } from 'redux-saga/effects'
import { push } from 'react-router-redux';
import { actionTypes } from '~/constants';
import fetch from '~/utils/fetch';
import awsconfig from '~/awsconfig';
import bluebird from 'bluebird';

const excludedFields = [
  '_id',
  'objectType',
  'createdAt',
  'updatedAt',
  'fromInternalOnlyClient',
  // 'vcaClaimId',
  'vcaClaimIdSort', // field created and only used in frontend for sorting in table
  'vcaApiError',
  'photos',
  'latitude',
  'longitude',
  'address',
  'activities',
  'lastActivityAt',
  'department',
  // 'reporter',
  // 'reporterEmail',
  'reporterVcaInsurerId',
  'role',
  'reporterName', // field only used in frontend
  '__user', // field only used in frontend
  '__department', // field only used in frontend
];

const requiredFieldsWithDefaults = {
  isArchived: false,
}

var Api = {
    fetchReports: async () => {
        console.log('fetchReports begin')
        let response = await fetch({
            url: `${awsconfig.officeApi}/reports`,
            method: 'get',
        })
        return response.result;
    },
    fetchReport: async (id) => {
        console.log('fetchReport begin')
        let response = await fetch({
            url: `${awsconfig.officeApi}/reports/${id}`,
            method: 'get',
        })
        return response.result;
    },
    createReport: async (data) => {
        console.log('createReport begin')
        // create a copy before deleting excluded keys
        var clone = JSON.parse(JSON.stringify(data));
        // include any required fields' defaults
        clone = {
          ...requiredFieldsWithDefaults,
          ...clone  // this will overwrite defaults
        }
        let response = await fetch({
            url: `${awsconfig.officeApi}/reports`,
            data: clone,
            method: 'post',
        })
        return response.result;
    },
    updateReport: async (id, data) => {
        console.log('updateReport begin')
        // create a copy before deleting excluded keys
        var clone = JSON.parse(JSON.stringify(data));
        // remove keys that API will not accept
        Object.keys(clone).map(key => {
          if (excludedFields.includes(key)) {
            delete clone[key];
          }
        });
        // include any required fields' defaults
        clone = {
          ...requiredFieldsWithDefaults,
          ...clone  // this will overwrite defaults
        }
        let response = await fetch({
            url: `${awsconfig.officeApi}/reports/${id}`,
            data: clone,
            method: 'put',
        })
        return response.result;
    },
    submitReportToVCA: async (id) => {
        console.log('submitReportToVCA begin')
        let response = await fetch({
            url: `${awsconfig.officeApi}/reports/${id}/vca`,
            data: {},
            method: 'post',
        })
        return response.result;
    },
}

function* fetchReports(action) {
    try {
        yield put({type: actionTypes.SPINNING_START})
        const reports = yield call(Api.fetchReports);
        yield put({type: actionTypes.REPORTS_FETCH_ALL_SUCCESS, reports: reports});
        yield put({
          type: actionTypes.SNACKBAR,
          snackbar: {
            severity: 'success',
            text: `Loaded ${reports.length} reports from the server.`
          }
        });
    } catch (e) {
        yield put({type: actionTypes.REPORTS_FETCH_ALL_ERROR, error: e.message});
        yield put({
          type: actionTypes.SNACKBAR,
          snackbar: {
            severity: 'error',
            text: e.message
          }
        });
    } finally {
        yield put({type: actionTypes.SPINNING_STOP})
    }
}

function* fetchReport(action) {
    try {
        yield put({type: actionTypes.SPINNING_START})
        const report = yield call(Api.fetchReport, action.id);
        yield put({type: actionTypes.REPORTS_FETCH_ONE_SUCCESS, detail: report});
        yield put({
          type: actionTypes.SNACKBAR,
          snackbar: {
            severity: 'success',
            text: `Loaded 1 report from the server.`
          }
        });
    } catch (e) {
        yield put({type: actionTypes.REPORTS_FETCH_ONE_ERROR, error: e.message});
        yield put({
          type: actionTypes.SNACKBAR,
          snackbar: {
            severity: 'error',
            text: e.message
          }
        });
    } finally {
        yield put({type: actionTypes.SPINNING_STOP})
    }
}

function* updateReport(action) {
    try {
        yield put({type: actionTypes.SPINNING_START})
        const report = yield call(Api.updateReport, action.id, action.data);
        yield put({type: actionTypes.REPORTS_UPDATE_ONE_SUCCESS, report: report});
        yield put({
          type: actionTypes.SNACKBAR,
          snackbar: {
            severity: 'success',
            text: `Updated 1 report.`
          }
        });
    } catch (e) {
        console.error(e)
        yield put({type: actionTypes.REPORTS_UPDATE_ONE_ERROR, error: e.message});
        yield put({
          type: actionTypes.SNACKBAR,
          snackbar: {
            severity: 'error',
            text: e.message
          }
        });
    } finally {
        yield put({type: actionTypes.SPINNING_STOP})
    }
}

function* createReport(action) {
    try {
        yield put({type: actionTypes.SPINNING_START})
        const report = yield call(Api.createReport, action.data);
        yield put({type: actionTypes.REPORTS_CREATE_ONE_SUCCESS, report: report});
        yield put({
          type: actionTypes.SNACKBAR,
          snackbar: {
            severity: 'success',
            text: `Created 1 report.`
          }
        });
        yield put({type: actionTypes.REPORTS_FETCH_ALL})
    } catch (e) {
        console.error(e)
        yield put({type: actionTypes.REPORTS_CREATE_ONE_ERROR, error: e.message});
        yield put({
          type: actionTypes.SNACKBAR,
          snackbar: {
            severity: 'error',
            text: e.message
          }
        });
    } finally {
        yield put({type: actionTypes.SPINNING_STOP})
    }
}

const MAX_VCA_ATTEMPTS = 5;
function* submitReportToVCA(action) {
    let n_attempts = 0;
    let report;
    try {
        yield put({type: actionTypes.SPINNING_START})
        report = yield call(Api.submitReportToVCA, action.id);
        yield put({
          type: actionTypes.SNACKBAR,
          snackbar: {
            severity: 'success',
            text: `Queued 1 report to submit to VCA.`
          }
        });
        while(n_attempts++ <= MAX_VCA_ATTEMPTS){
          console.log('Waiting 2 seconds...');
          yield call(bluebird.delay, 2000);  // wait 2 second
          console.log('Attempting to fetch vcaClaimId...');
          report = yield call(Api.fetchReport, action.id);
          if(report.vcaClaimId){
            yield put({type: actionTypes.REPORTS_FETCH_ONE_SUCCESS, detail: report});
            yield put({
              type: actionTypes.SNACKBAR,
              snackbar: {
                severity: 'success',
                text: `The report was successfully received by VCA as new claim ID ${report.vcaClaimId}.`
              }
            });
            return;
          }
        }
    } catch (e) {
        console.error(e)
        yield put({
          type: actionTypes.SNACKBAR,
          snackbar: {
            severity: 'error',
            text: e.message
          }
        });
    } finally {
        yield put({type: actionTypes.SPINNING_STOP})
    }
}

// throttle (e.g. for autocomplete search)
// takeEvery
// takeLatest
export const sagas = {
    fetchReportsSaga: takeLatest(actionTypes.REPORTS_FETCH_ALL, fetchReports),
    fetchReportSaga: takeLatest(actionTypes.REPORTS_FETCH_ONE, fetchReport),
    createReportSaga: takeLatest(actionTypes.REPORTS_CREATE_ONE, createReport),
    updateReportSaga: takeLatest(actionTypes.REPORTS_UPDATE_ONE, updateReport),
    submitReportToVCASaga: takeLatest(actionTypes.REPORTS_SUBMIT_ONE_VCA, submitReportToVCA),
}
